import axios from "axios";
import { unauthorizedInterceptors } from "../utilities/functions";

// Configure the axios instance
const databaseAPI = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/customer`, // Using relative URL to avoid environment variable issues
  headers: {  
    "Content-Type": "application/json",
  },
});
unauthorizedInterceptors(databaseAPI);

// Set Authorization Header
const setAuthHeader = () => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    databaseAPI.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
};

// === SEQ File Endpoints ===
export const getSeqFiles = async () => {
  setAuthHeader();
  return await databaseAPI.get("/seq-files/");
};

export const uploadSeqFile = async (formData) => {
  setAuthHeader();
  return await databaseAPI.post("/upload-seq-file/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const processSeqFile = async (fileId) => {
  setAuthHeader();
  return await databaseAPI.post(`/seq-files/${fileId}/process/`);
};

// === Mail Files Report Endpoints ===
export const getMailFilesReport = async (
  clientIds = ['all'], 
  category = 'all', 
  mailingLists = ['all'],
  dateRange = { startDate: null, endDate: null }
) => {
  setAuthHeader();
  
  // Ensure clientIds is an array
  const clientIdsArray = Array.isArray(clientIds) ? clientIds : [clientIds];
  
  // Ensure mailingLists is an array
  const mailingListsArray = Array.isArray(mailingLists) ? mailingLists : [mailingLists];
  
  // Create params object with client_ids array and category
  const params = {
    'client_ids[]': clientIdsArray
  };
  
  // Add category if it's not 'all'
  if (category && category !== 'all') {
    params.category = category;
  }
  
  // Add mailing lists if not 'all'
  if (mailingListsArray.length > 0 && !mailingListsArray.includes('all')) {
    params['mailing_lists[]'] = mailingListsArray;
  }
  
  // Add date range parameters if provided
  if (dateRange.startDate) {
    params.start_date = dateRange.startDate;
  }
  
  if (dateRange.endDate) {
    params.end_date = dateRange.endDate;
  }
  
  return await databaseAPI.get("/mail-files-report/", { params });
};

export const getMailFilesReportPeopleDetails = async (
  listCount,
  clientIds = ['all'], 
  category = 'all', 
  mailingLists = ['all'],
  dateRange = { startDate: null, endDate: null },
  password = ''
) => {
  setAuthHeader();
  
  // Ensure clientIds is an array
  const clientIdsArray = Array.isArray(clientIds) ? clientIds : [clientIds];
  
  // Ensure mailingLists is an array
  const mailingListsArray = Array.isArray(mailingLists) ? mailingLists : [mailingLists];
  
  // Create params object with list_count, password, and client_ids array
  const params = {
    list_count: listCount,
    password: password,
    'client_ids[]': clientIdsArray
  };
  
  // Add category if it's not 'all'
  if (category && category !== 'all') {
    params.category = category;
  }
  
  // Add mailing lists if not 'all'
  if (mailingListsArray.length > 0 && !mailingListsArray.includes('all')) {
    params['mailing_lists[]'] = mailingListsArray;
  }
  
  // Add date range parameters if provided
  if (dateRange.startDate) {
    params.start_date = dateRange.startDate;
  }
  
  if (dateRange.endDate) {
    params.end_date = dateRange.endDate;
  }
  
  return await databaseAPI.get("/mail-files-people-details/", { params });
};

// Get all unique mailing lists
export const getMailFilesTimelineReport = async (
  clientIds = ['all'], 
  category = 'all', 
  mailingLists = ['all'],
  listCount = null
) => {
  setAuthHeader();
  
  // Ensure clientIds is an array
  const clientIdsArray = Array.isArray(clientIds) ? clientIds : [clientIds];
  
  // Ensure mailingLists is an array
  const mailingListsArray = Array.isArray(mailingLists) ? mailingLists : [mailingLists];
  
  // Create params object with client_ids array and category
  const params = {
    'client_ids[]': clientIdsArray
  };
  
  // Add category if it's not 'all'
  if (category && category !== 'all') {
    params.category = category;
  }
  
  // Add mailing lists if not 'all'
  if (mailingListsArray.length > 0 && !mailingListsArray.includes('all')) {
    params['mailing_lists[]'] = mailingListsArray;
  }
  
  // Add list count filter if provided
  if (listCount && listCount !== 'all') {
    params.list_count = listCount;
  }
  
  
  try {
    const response = await databaseAPI.get("/mail-files-timeline-report/", { params });
    return response;
  } catch (error) {
    console.error('Timeline API error:', error.response ? error.response.status : error.message);
    throw error;
  }
};

export const getMailFilesTimelinePeopleDetails = async (
  activeYears,
  clientIds = ['all'], 
  category = 'all', 
  mailingLists = ['all'],
  password = ''
) => {
  setAuthHeader();
  
  // Ensure clientIds is an array
  const clientIdsArray = Array.isArray(clientIds) ? clientIds : [clientIds];
  
  // Ensure mailingLists is an array
  const mailingListsArray = Array.isArray(mailingLists) ? mailingLists : [mailingLists];
  
  // Create params object with active_years, password, and client_ids array
  const params = {
    active_years: activeYears,
    password: password,
    'client_ids[]': clientIdsArray
  };
  
  // Add category if it's not 'all'
  if (category && category !== 'all') {
    params.category = category;
  }
  
  // Add mailing lists if not 'all'
  if (mailingListsArray.length > 0 && !mailingListsArray.includes('all')) {
    params['mailing_lists[]'] = mailingListsArray;
  }
  
  try {
    const response = await databaseAPI.get("/mail-files-timeline-people-details/", { params });
    return response;
  } catch (error) {
    console.error('Timeline people details API error:', error.response ? error.response.status : error.message);
    throw error;
  }
};

export const getMailingLists = async () => {
  setAuthHeader();
  return await databaseAPI.get("/mailing-lists/");
};

// === Internal Customer Endpoints ===
export const getInternalCustomers = async (params = {}) => {
  setAuthHeader();
  return await databaseAPI.get("/internal-customers/", { params });
};

export const getInternalCustomerStats = async () => {
  setAuthHeader();
  return await databaseAPI.get("/internal-customers/stats/");
};

export const getInternalCustomerById = async (id) => {
  setAuthHeader();
  return await databaseAPI.get(`/internal-customers/${id}/`);
};

export const getInternalCustomerDetails = async (id) => {
  setAuthHeader();
  return await databaseAPI.get(`/internal-customers/${id}/details/`);
};

// === Monthly Timeline Report Endpoints ===
export const getMailFilesTimelineMonthlyReport = async (
  clientIds = ['all'], 
  category = 'all', 
  mailingLists = ['all'],
  listCount = null
) => {
  setAuthHeader();
  
  // Ensure clientIds is an array
  const clientIdsArray = Array.isArray(clientIds) ? clientIds : [clientIds];
  
  // Ensure mailingLists is an array
  const mailingListsArray = Array.isArray(mailingLists) ? mailingLists : [mailingLists];
  
  // Build the query string
  const params = new URLSearchParams();
  
  // Add client IDs
  clientIdsArray.forEach(id => {
    params.append('client_ids[]', id);
  });
  
  // Add category
  params.append('category', category);
  
  // Add mailing lists
  mailingListsArray.forEach(list => {
    params.append('mailing_lists[]', list);
  });
  
  // Add list count if provided
  if (listCount !== null && listCount !== 'all') {
    params.append('list_count', listCount);
  }
  
  return await databaseAPI.get(`/mail-files-timeline-monthly-report/?${params.toString()}`);
};

export const getMailFilesTimelineMonthlyDetails = async (
  year,
  month,
  password = '',
  clientIds = ['all'], 
  category = 'all', 
  mailingLists = ['all']
) => {
  setAuthHeader();
  
  // Ensure clientIds is an array
  const clientIdsArray = Array.isArray(clientIds) ? clientIds : [clientIds];
  
  // Ensure mailingLists is an array
  const mailingListsArray = Array.isArray(mailingLists) ? mailingLists : [mailingLists];
  
  // Build the query string
  const params = new URLSearchParams();
  
  // Add year and month
  params.append('year', year);
  params.append('month', month);
  
  // Add password
  params.append('password', password);
  
  // Add client IDs
  clientIdsArray.forEach(id => {
    params.append('client_ids[]', id);
  });
  
  // Add category
  params.append('category', category);
  
  // Add mailing lists
  mailingListsArray.forEach(list => {
    params.append('mailing_lists[]', list);
  });
  
  return await databaseAPI.get(`/mail-files-timeline-monthly-details/?${params.toString()}`);
};
